import { useState } from 'react'
import type { OrganisationKind } from '../api/types'
import { useAuthenticatedSession } from '../auth/session'
import { useT } from '../i18n'
import { SelectField, useToast } from '../ui'
import { apiErrorText } from '../bff/errorText'

export interface BillerSwitcherProps {
  /** Billers the internal user may view. Supplied by the caller because the
   *  list comes from GET /v1/billers, which belongs to the screen layer. */
  readonly billers: ReadonlyArray<{ readonly id: string; readonly name: string; readonly kind: OrganisationKind }>
}

/** Clearing the selection is a real choice, not a null state: an internal user
 *  with no biller selected sees every gateway on the platform, which is how
 *  they answer a question that crosses biller boundaries. Blocking the empty
 *  value — as this control used to — made the whole-platform view reachable
 *  only by signing out and back in. */
const ALL_BILLERS = ''

/**
 * Internal users only. The selection is written to SERVER-SIDE session state
 * via POST /bff/biller — the browser never sends an X-Biller-Id header, and
 * the BFF's proxy sets it from the session. That indirection is the whole
 * tenant-isolation boundary, so this component deliberately has no way to
 * express "just send this id with the next request".
 */
export function BillerSwitcher({ billers }: BillerSwitcherProps) {
  const { user, switchBiller } = useAuthenticatedSession()
  const { notify } = useToast()
  const t = useT()
  const [pending, setPending] = useState(false)

  if (!user.isInternal) return null

  return (
    <SelectField
      label={t('shell.selectBiller')}
      value={user.billerId ?? ''}
      disabled={pending}
      onChange={(event) => {
        const next = event.target.value
        setPending(true)
        switchBiller(next)
          .catch((error: unknown) =>
            notify('danger', apiErrorText(t, error).text),
          )
          .finally(() => setPending(false))
      }}
    >
      <option value={ALL_BILLERS}>{t('shell.allBillers')}</option>
      {/* Grouped by kind (V1.1): "act for" works for either, but an operator
          picking an insurer should not have to know it is not a biller. An
          empty group is omitted rather than shown as an empty heading. */}
      {(['provider', 'beneficiary'] as const).map((kind) => {
        const members = billers.filter((b) => b.kind === kind)
        if (members.length === 0) return null
        return (
          <optgroup key={kind} label={kind === 'provider' ? t('shell.group.billers') : t('shell.group.beneficiaries')}>
            {members.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </optgroup>
        )
      })}
    </SelectField>
  )
}
